import { Modal } from '@/components/modal/modal';
import { toaster } from '@/components/ui/toaster';
import type { InstagramPage } from '@/services/instagram-service';
import { useInstagramStore } from '@/stores/integrations/instagram-store';
import { Box, Button, Spinner, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface InstagramPageSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  pages: InstagramPage[];
  brandId: string;
  onSuccess: () => void;
}

const InstagramPageSelectModal = ({
  isOpen,
  onClose,
  pages,
  brandId,
  onSuccess,
}: InstagramPageSelectModalProps) => {
  const { t } = useTranslation();
  const { finalizeConnection, isLoading } = useInstagramStore();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  const handlePageSelect = async (page: InstagramPage) => {
    if (isLoading) return;
    setSelectedPageId(page.id);
    try {
      await finalizeConnection(page, brandId);

      toaster.create({
        description: t('integrations.instagram.success.connected'),
        type: 'success',
      });

      onSuccess();
      onClose();
    } catch (error) {
      toaster.create({
        description: t('integrations.instagram.errors.connectionFailed'),
        type: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('integrations.instagram.selectPageTitle')}>
      <Stack gap={4}>
        {pages.map((page) => (
          <Box
            key={page.id}
            p={4}
            borderWidth="1px"
            rounded="md"
            cursor={isLoading ? 'not-allowed' : 'pointer'}
            opacity={isLoading ? 0.6 : 1}
            _hover={isLoading ? {} : { bg: 'gray.50' }}
            onClick={() => handlePageSelect(page)}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Text fontWeight="medium">{page.name}</Text>
              {isLoading && selectedPageId === page.id && <Spinner size="sm" />}
            </Box>
          </Box>
        ))}
      </Stack>
      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </Box>
    </Modal>
  );
};

export default InstagramPageSelectModal;
